import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, MAT_TYPE } from "@/lib/ui";
import { toast } from "sonner";
import { Search, ChevronDown, ChevronRight, BookText, UploadCloud, Loader2, X, Pencil, Plus, Trash2 } from "lucide-react";

export default function Recipes() {
  const { isEditor } = useAuth();
  const [data, setData] = useState({ total: 0, recipes: [] });
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();
  const debounce = useRef();
  const [mats, setMats] = useState([]);
  const [editRec, setEditRec] = useState(null);
  const [savingRec, setSavingRec] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newRec, setNewRec] = useState({ order_id: "", size: "", wire_raw: "", total_coil: "", lines: [] });
  const [savingNew, setSavingNew] = useState(false);

  const load = (q) => {
    setLoading(true);
    api.get(`/recipes?search=${encodeURIComponent(q)}&limit=50`).then((r) => {
      setData(r.data);
      setLoading(false);
    });
  };
  useEffect(() => { load(""); }, []);
  useEffect(() => { api.get("/materials").then((r) => setMats(r.data)).catch(() => {}); }, []);

  const openEditRec = (r) => setEditRec({ ...r, lines: r.lines.map((l) => ({ ...l })) });
  const setLine = (i, patch) => setEditRec((er) => ({ ...er, lines: er.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const saveRec = async () => {
    setSavingRec(true);
    try {
      await api.put(`/recipes/${encodeURIComponent(editRec.order_id)}`, {
        lines: editRec.lines.filter((l) => l.key).map((l) => ({ ...l, qty_per_unit: parseFloat(l.qty_per_unit) || 0 })),
        size: editRec.size, wire_raw: editRec.wire_raw,
      });
      toast.success("Reçete güncellendi");
      setEditRec(null); load(search);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingRec(false);
  };

  const onSearch = (v) => {
    setSearch(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(v), 350);
  };

  const addEditLine = () => setEditRec((er) => ({ ...er, lines: [...er.lines, { kind: "tel", key: "", name: "", unit: "kg", qty_per_unit: "" }] }));
  const removeEditLine = (i) => setEditRec((er) => ({ ...er, lines: er.lines.filter((_, j) => j !== i) }));
  const deleteRecipe = async (orderId) => {
    if (!window.confirm(`Order ${orderId} reçetesi silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/recipes/${encodeURIComponent(orderId)}`);
      toast.success("Reçete silindi");
      load(search);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doImport = async (f) => {
    if (!f) return;
    setImporting(true);
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/recipes/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(`${data.recipes} reçete, ${data.materials} malzeme içe aktarıldı`);
      setImportOpen(false);
      load(search);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "İçe aktarma başarısız");
    }
    setImporting(false);
  };

  const addNewLine = () => setNewRec((r) => ({ ...r, lines: [...r.lines, { kind: "tel", key: "", name: "", unit: "kg", qty_per_unit: "" }] }));
  const setNewLine = (i, patch) => setNewRec((r) => ({ ...r, lines: r.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const removeNewLine = (i) => setNewRec((r) => ({ ...r, lines: r.lines.filter((_, j) => j !== i) }));
  const createRecipe = async () => {
    if (!newRec.order_id.trim()) { toast.error("Sıra No (Order ID) gerekli"); return; }
    setSavingNew(true);
    try {
      const lines = newRec.lines.filter((l) => l.key).map((l) => {
        const m = mats.find((x) => x.key === l.key) || {};
        return {
          kind: m.type || l.kind, key: l.key, name: m.name || l.name, unit: m.unit || l.unit,
          qty_per_unit: parseFloat(l.qty_per_unit) || 0,
          ...(m.type === "tela" ? { color: m.color, gramaj: m.gramaj, width: m.width } : {}),
        };
      });
      await api.post("/recipes", {
        order_id: newRec.order_id.trim(), size: newRec.size || null, wire_raw: newRec.wire_raw || null,
        total_coil: newRec.total_coil ? parseFloat(newRec.total_coil) : null, lines,
      });
      toast.success("Reçete oluşturuldu");
      setNewOpen(false);
      setNewRec({ order_id: "", size: "", wire_raw: "", total_coil: "", lines: [] });
      load(search);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingNew(false);
  };

  return (
    <div className="space-y-6 animate-in-up" data-testid="recipes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight">Reçeteler</h1>
          <p className="text-[#94A3B8] text-sm mt-1">
            Order ID ile arayın — <span className="font-num">{fmt(data.total)}</span> reçete kayıtlı
          </p>
        </div>
        {isEditor && (
          <div className="flex items-center gap-2">
            <button
              data-testid="recipe-import-button"
              onClick={() => setImportOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#131A26] border border-[#212D40] hover:border-[#2563EB] text-sm font-medium"
            >
              <UploadCloud className="w-4 h-4" /> Reçete Excel Yükle
            </button>
            <button
              data-testid="recipe-new-button"
              onClick={() => setNewOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Yeni Reçete Oluştur
            </button>
          </div>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="recipe-search-input"
          placeholder="Order ID, ölçü veya açıklama ara..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="w-full bg-[#131A26] border border-[#212D40] rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#2563EB]"
        />
      </div>

      {loading && <div className="text-[#94A3B8] text-sm">Yükleniyor...</div>}

      <div className="space-y-2">
        {data.recipes.map((r) => (
          <div key={r.order_id} className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
            <div
              className="flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-[#0F1520]"
              onClick={() => setExpanded(expanded === r.order_id ? null : r.order_id)}
              data-testid={`recipe-row-${r.order_id}`}
            >
              {expanded === r.order_id ? <ChevronDown className="w-4 h-4 text-[#94A3B8]" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8]" />}
              <BookText className="w-4 h-4 text-[#2563EB]" />
              <span className="font-num font-semibold w-24">{r.order_id}</span>
              <span className="text-sm text-[#94A3B8] w-20">{r.size || "—"}</span>
              <span className="text-sm flex-1 truncate">{r.description || r.frame || ""}</span>
              <span className="text-xs text-[#64748B]">{r.lines.length} malzeme</span>
            </div>
            {expanded === r.order_id && (
              <div className="px-5 pb-4 pt-1 border-t border-[#212D40]">
                <div className="text-xs text-[#64748B] mb-2 font-num">
                  Tel: {r.wire_raw || "—"} · Frame: {r.frame || "—"}
                </div>
                {isEditor && (
                  <div className="mb-3 flex items-center gap-2">
                    <button data-testid={`recipe-edit-${r.order_id}`} onClick={() => openEditRec(r)}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#0F1520] border border-[#212D40] hover:border-[#2563EB]">
                      <Pencil className="w-3.5 h-3.5" /> Reçeteyi Düzenle
                    </button>
                    <button data-testid={`recipe-delete-${r.order_id}`} onClick={() => deleteRecipe(r.order_id)}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] text-[#EF4444] hover:bg-[rgba(239,68,68,0.2)]">
                      <Trash2 className="w-3.5 h-3.5" /> Reçeteyi Sil
                    </button>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-[#94A3B8] uppercase tracking-wider">
                      <th className="py-2">Malzeme</th>
                      <th className="py-2">Tür</th>
                      <th className="py-2 text-right">Adet Başına</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.lines.map((l, i) => {
                      const t = MAT_TYPE[l.kind] || {};
                      return (
                        <tr key={i} className="border-t border-[#1a2334]">
                          <td className="py-2">{l.name}</td>
                          <td className="py-2">
                            <span className="text-xs px-2 py-0.5 rounded" style={{ color: t.color, background: `${t.color}22` }}>
                              {t.label}
                            </span>
                          </td>
                          <td className="py-2 text-right font-num">{fmt(l.qty_per_unit)} {l.unit}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
        {!loading && data.recipes.length === 0 && (
          <div className="text-[#64748B] text-sm">Sonuç bulunamadı.</div>
        )}
      </div>

      {editRec && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold">Reçete Düzenle — {editRec.order_id}</h3>
              <button onClick={() => setEditRec(null)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Ölçü</label>
                  <input data-testid="recipe-edit-size" value={editRec.size || ""} onChange={(e) => setEditRec({ ...editRec, size: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Tel (açıklama)</label>
                  <input data-testid="recipe-edit-wire" value={editRec.wire_raw || ""} onChange={(e) => setEditRec({ ...editRec, wire_raw: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Malzeme Satırları (adet başına)</div>
                  <button data-testid="recipe-edit-add-line" onClick={addEditLine} className="text-sm text-[#2563EB] hover:underline flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Satır ekle
                  </button>
                </div>
                {editRec.lines.map((l, i) => {
                  const t = MAT_TYPE[l.kind] || {};
                  const opts = mats.filter((m) => m.type === l.kind);
                  const editable = l.kind === "tel" || l.kind === "tela";
                  const isNew = !l.key;
                  return (
                    <div key={i} className="flex items-center gap-2" data-testid={`recipe-edit-line-${i}`}>
                      <span className="text-xs px-2 py-0.5 rounded shrink-0" style={{ color: t.color, background: `${t.color}22` }}>{t.label}</span>
                      {isNew ? (
                        <select value="" data-testid={`recipe-edit-line-mat-${i}`}
                          onChange={(e) => { const m = mats.find((x) => x.key === e.target.value); if (m) setLine(i, { key: m.key, kind: m.type, name: m.name, unit: m.unit, gramaj: m.gramaj, width: m.width, color: m.color }); }}
                          className="flex-1 min-w-0 bg-[#0F1520] border border-[#212D40] rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#2563EB]">
                          <option value="">Malzeme seçin...</option>
                          {mats.map((m) => <option key={m.id} value={m.key}>{(MAT_TYPE[m.type]?.label || m.type)} · {m.name}</option>)}
                        </select>
                      ) : editable ? (
                        <select value={l.key} data-testid={`recipe-edit-line-mat-${i}`}
                          onChange={(e) => { const m = mats.find((x) => x.key === e.target.value); if (m) setLine(i, { key: m.key, name: m.name, gramaj: m.gramaj, width: m.width, color: m.color }); }}
                          className="flex-1 min-w-0 bg-[#0F1520] border border-[#212D40] rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#2563EB]">
                          {!opts.some((m) => m.key === l.key) && <option value={l.key}>{l.name}</option>}
                          {opts.map((m) => <option key={m.id} value={m.key}>{m.name}</option>)}
                        </select>
                      ) : (
                        <span className="flex-1 min-w-0 text-sm truncate">{l.name}</span>
                      )}
                      <input type="number" data-testid={`recipe-edit-line-qty-${i}`} value={l.qty_per_unit}
                        onChange={(e) => setLine(i, { qty_per_unit: e.target.value })}
                        className="w-28 bg-[#0F1520] border border-[#212D40] rounded-lg px-2 py-1.5 text-sm text-right font-num outline-none focus:border-[#2563EB]" />
                      <span className="text-xs text-[#64748B] w-8">{l.unit}</span>
                      <button data-testid={`recipe-edit-line-remove-${i}`} onClick={() => removeEditLine(i)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  );
                })}
                {editRec.lines.length === 0 && <div className="text-xs text-[#64748B]">Tüm satırlar kaldırıldı. "Satır ekle" ile yeniden ekleyin.</div>}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setEditRec(null)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="recipe-edit-save" onClick={saveRec} disabled={savingRec}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">{savingRec ? "Kaydediliyor..." : "Kaydet"}</button>
            </div>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold">Reçete Excel Yükle</h3>
              <button onClick={() => setImportOpen(false)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5">
              <div className="text-xs text-[#94A3B8] bg-[#0F1520] border border-[#212D40] rounded-lg p-3 mb-4">
                Fabrika reçete dosyanızı (.xls / .xlsx) yükleyin. Sistem Order ID tablosunu otomatik
                bulur, tel/tela/kraft/lamet/c-ring malzemelerini üretir. Mevcut stoklar korunur.
              </div>
              <div
                onClick={() => !importing && fileRef.current?.click()}
                className="border-2 border-dashed border-[#212D40] hover:border-[#2563EB] rounded-xl p-10 text-center cursor-pointer transition-colors"
                data-testid="recipe-import-dropzone"
              >
                {importing ? (
                  <div className="flex flex-col items-center gap-2 text-[#94A3B8]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
                    İçe aktarılıyor, lütfen bekleyin...
                  </div>
                ) : (
                  <>
                    <UploadCloud className="w-10 h-10 mx-auto text-[#2563EB] mb-3" />
                    <div className="font-medium">Dosya seçin (.xls / .xlsx)</div>
                  </>
                )}
                <input ref={fileRef} type="file" accept=".xls,.xlsx,.csv" className="hidden"
                  onChange={(e) => doImport(e.target.files[0])} />
              </div>
            </div>
          </div>
        </div>
      )}
      {newOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold">Yeni Reçete Oluştur</h3>
              <button onClick={() => setNewOpen(false)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Sıra No (Order ID) *</label>
                  <input data-testid="new-recipe-order-id" value={newRec.order_id} onChange={(e) => setNewRec({ ...newRec, order_id: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Ölçü</label>
                  <input data-testid="new-recipe-size" value={newRec.size} onChange={(e) => setNewRec({ ...newRec, size: e.target.value })}
                    placeholder="70 x 190" className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Tel (açıklama)</label>
                  <input data-testid="new-recipe-wire" value={newRec.wire_raw} onChange={(e) => setNewRec({ ...newRec, wire_raw: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Yay/adet (Mesai için)</label>
                  <input data-testid="new-recipe-coil" type="number" value={newRec.total_coil} onChange={(e) => setNewRec({ ...newRec, total_coil: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Malzeme Satırları (adet başına)</div>
                  <button data-testid="new-recipe-add-line" onClick={addNewLine} className="text-sm text-[#2563EB] hover:underline flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Satır ekle
                  </button>
                </div>
                {newRec.lines.map((l, i) => {
                  const cur = mats.find((x) => x.key === l.key) || {};
                  return (
                    <div key={i} className="flex items-center gap-2" data-testid={`new-recipe-line-${i}`}>
                      <select value={l.key} data-testid={`new-recipe-line-mat-${i}`}
                        onChange={(e) => { const m = mats.find((x) => x.key === e.target.value); setNewLine(i, { key: e.target.value, kind: m?.type || l.kind, name: m?.name, unit: m?.unit }); }}
                        className="flex-1 min-w-0 bg-[#0F1520] border border-[#212D40] rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#2563EB]">
                        <option value="">Malzeme seçin...</option>
                        {mats.map((m) => <option key={m.id} value={m.key}>{(MAT_TYPE[m.type]?.label || m.type)} · {m.name}</option>)}
                      </select>
                      <input type="number" data-testid={`new-recipe-line-qty-${i}`} value={l.qty_per_unit}
                        onChange={(e) => setNewLine(i, { qty_per_unit: e.target.value })}
                        className="w-28 bg-[#0F1520] border border-[#212D40] rounded-lg px-2 py-1.5 text-sm text-right font-num outline-none focus:border-[#2563EB]" />
                      <span className="text-xs text-[#64748B] w-8">{cur.unit || l.unit}</span>
                      <button onClick={() => removeNewLine(i)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  );
                })}
                {newRec.lines.length === 0 && <div className="text-xs text-[#64748B]">Henüz satır yok. "Satır ekle" ile malzeme ekleyin.</div>}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setNewOpen(false)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="new-recipe-save" onClick={createRecipe} disabled={savingNew}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">{savingNew ? "Kaydediliyor..." : "Oluştur"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
